import { GetStaticProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { LogoSvg } from '../../assets/icons/LogoSvg';
import theme from '../../assets/theme';
import { DefaultLocale } from '../../providers/tolgee';
import {
  CheckResult,
  CheckStatus,
  DiagnosticCheck,
  generateITAdminMessage,
  getChecks,
  getCuriLiveDiagnoseUrl,
  getSmartHints,
  runChecks,
  SmartHint,
} from '../../utils/diagnostic-checks';

type Phase = 'ready' | 'running' | 'done';

const DiagnosePage = () => {
  const [phase, setPhase] = useState<Phase>('ready');
  const [checks, setChecks] = useState<DiagnosticCheck[]>([]);
  const [results, setResults] = useState<Map<string, CheckResult>>(new Map());
  const [hints, setHints] = useState<SmartHint[]>([]);
  const [copied, setCopied] = useState(false);
  const [curiLiveUrl, setCuriLiveUrl] = useState('https://curi.live/diagnose');
  const resultsRef = useRef<CheckResult[]>([]);

  useEffect(() => {
    setChecks(getChecks());
    setCuriLiveUrl(getCuriLiveDiagnoseUrl());
  }, []);

  const handleRun = useCallback(async () => {
    setPhase('running');
    setResults(new Map());
    setHints([]);
    setCopied(false);
    resultsRef.current = [];

    const finalResults = await runChecks(checks, (id, result) => {
      setResults(prev => new Map(prev).set(id, result));
    });

    resultsRef.current = finalResults;
    setHints(getSmartHints(finalResults, checks));
    setPhase('done');
  }, [checks]);

  const handleCopy = useCallback(async () => {
    const message = generateITAdminMessage(resultsRef.current, checks);
    await navigator.clipboard.writeText(message);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [checks]);

  const handleRunAgain = useCallback(() => {
    setPhase('ready');
    setResults(new Map());
    setHints([]);
    setCopied(false);
  }, []);

  const allPassed =
    phase === 'done' &&
    checks.every(c => {
      const r = results.get(c.id);
      return r?.status === 'pass';
    });

  const hasFails =
    phase === 'done' &&
    checks.some(c => {
      const r = results.get(c.id);
      return r?.status === 'fail';
    });

  return (
    <>
      <Head>
        <meta name="robots" content="noindex, nofollow" />
        <title>Connectivity Diagnostic | Curipod</title>
      </Head>
      <PageWrapper>
        <Card>
          <LogoWrapper>
            <LogoSvg />
          </LogoWrapper>

          {phase === 'ready' && (
            <>
              <Headline>Will Curipod work in your classroom?</Headline>
              <Description>
                Run a quick check to see if your network can reach all the services
                Curipod needs. Takes about 5 seconds — no IT knowledge needed!
              </Description>
              <PrimaryButton onClick={handleRun} disabled={checks.length === 0}>
                Run Diagnostic
              </PrimaryButton>
              <TipNote>
                Tip: Run this check on both teacher and student devices while connected to
                the school network.
              </TipNote>
              <GuideLink>
                Looking for the full troubleshooting guide?{' '}
                <Link href="/connectivity-issues">
                  <a>View guide</a>
                </Link>
              </GuideLink>
            </>
          )}

          {phase === 'running' && (
            <>
              <Headline>Checking your connection...</Headline>
              <CheckList>
                {checks.map(check => {
                  const result = results.get(check.id);
                  return (
                    <CheckRow key={check.id} $status={result?.status}>
                      <CheckIcon $status={result?.status} />
                      <CheckInfo>
                        <CheckLabel>{check.label}</CheckLabel>
                        <CheckDesc>{check.description}</CheckDesc>
                      </CheckInfo>
                    </CheckRow>
                  );
                })}
              </CheckList>
            </>
          )}

          {phase === 'done' && (
            <>
              {allPassed ? (
                <ResultHeadline $variant="success">
                  All clear! Curipod will work on your network.
                </ResultHeadline>
              ) : (
                <ResultHeadline $variant="error">
                  {hasFails
                    ? "Don't worry — your IT team can fix this quickly."
                    : 'Almost there — one minor thing to check.'}
                </ResultHeadline>
              )}
              <CheckList>
                {checks.map(check => {
                  const result = results.get(check.id);
                  return (
                    <CheckRow key={check.id} $status={result?.status}>
                      <CheckIcon $status={result?.status} />
                      <CheckInfo>
                        <CheckLabel>{check.label}</CheckLabel>
                        <CheckDesc>
                          {result?.status === 'pass'
                            ? `Connected (${result.latencyMs}ms)`
                            : result?.status === 'warning'
                            ? 'Could not verify automatically'
                            : `Blocked — ${check.allowlistDomain} needs to be allowlisted`}
                        </CheckDesc>
                      </CheckInfo>
                    </CheckRow>
                  );
                })}
              </CheckList>

              {hints.length > 0 && (
                <HintsSection>
                  {hints.map((hint, i) => (
                    <HintCard key={i}>{hint.text}</HintCard>
                  ))}
                </HintsSection>
              )}

              <ButtonRow>
                <GhostButton onClick={handleCopy}>
                  {copied ? 'Copied!' : 'Copy report for IT admin'}
                </GhostButton>
                <GhostButton onClick={handleRunAgain}>Run again</GhostButton>
                <GhostButton
                  as="a"
                  href={curiLiveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Test student connectivity
                </GhostButton>
              </ButtonRow>

              <GuideLink>
                Need more help?{' '}
                <Link href="/connectivity-issues">
                  <a>View the full troubleshooting guide</a>
                </Link>
              </GuideLink>
            </>
          )}
        </Card>
      </PageWrapper>
    </>
  );
};

export default DiagnosePage;

export const getStaticProps: GetStaticProps = async () => ({
  props: {
    _tolgeeSSR: {
      language: DefaultLocale,
    },
  },
});

// --- Styled Components ---

const PageWrapper = styled.div`
  background: ${theme.colors.paleBeige};
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.08);
  max-width: 640px;
  width: 100%;
  padding: 48px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  @media (max-width: 992px) {
    padding: 32px 20px;
  }
`;

const LogoWrapper = styled.div`
  color: ${theme.colors.orange};
  margin-bottom: 24px;

  svg {
    width: 110px;
    height: auto;
  }
`;

const Headline = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${theme.colors.black};
  margin: 0 0 12px;

  @media (max-width: 320px) {
    font-size: 20px;
  }
`;

const Description = styled.p`
  font-size: 16px;
  color: ${theme.colors.greyVeryDark};
  margin: 0 0 28px;
  line-height: 1.5;
`;

const PrimaryButton = styled.button`
  background: ${theme.colors.orange};
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 14px 40px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${theme.colors.orangeDark};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TipNote = styled.p`
  font-size: 13px;
  color: ${theme.colors.subtitleGrey};
  margin: 16px 0 0;
`;

const GuideLink = styled.p`
  font-size: 14px;
  color: ${theme.colors.subtitleGrey};
  margin: 20px 0 0;

  a {
    color: ${theme.colors.orange};
    text-decoration: underline;
  }
`;

const CheckList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 20px 0;
`;

function getBorderColor(status?: CheckStatus) {
  switch (status) {
    case 'pass':
      return theme.colors.green;
    case 'fail':
      return theme.colors.red;
    case 'warning':
      return theme.colors.orange;
    default:
      return '#e0e0e0';
  }
}

const CheckRow = styled.div<{ $status?: CheckStatus }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #fff;
  border-radius: 5px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  border-left: 4px solid ${p => getBorderColor(p.$status)};
  text-align: left;
  transition: border-color 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const CheckIcon = styled.div<{ $status?: CheckStatus }>`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;

  ${p => {
    switch (p.$status) {
      case 'pass':
        return css`
          background: ${theme.colors.green};
          color: #fff;
          &::after {
            content: '\\2713';
          }
        `;
      case 'fail':
        return css`
          background: ${theme.colors.red};
          color: #fff;
          &::after {
            content: '\\2717';
          }
        `;
      case 'warning':
        return css`
          background: ${theme.colors.orange};
          color: #fff;
          &::after {
            content: '!';
          }
        `;
      case 'running':
        return css`
          border: 3px solid ${theme.colors.divider};
          border-top-color: ${theme.colors.orange};
          animation: ${spin} 0.7s linear infinite;
        `;
      default:
        return css`
          background: #f0f0f0;
        `;
    }
  }}
`;

const CheckInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const CheckLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${theme.colors.black};
`;

const CheckDesc = styled.span`
  font-size: 13px;
  color: ${theme.colors.subtitleGrey};
`;

const ResultHeadline = styled.h2<{ $variant: 'success' | 'error' }>`
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 8px;
  color: ${p => (p.$variant === 'success' ? theme.colors.green : theme.colors.black)};
`;

const HintsSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 8px 0 16px;
`;

const HintCard = styled.div`
  background: rgba(249, 151, 70, 0.08);
  border-left: 3px solid ${theme.colors.orange};
  border-radius: 5px;
  padding: 10px 14px;
  font-size: 13px;
  color: ${theme.colors.greyVeryDark};
  text-align: left;
  line-height: 1.4;
`;

const GhostButton = styled.button`
  background: transparent;
  border: 1px solid ${theme.colors.divider};
  border-radius: 5px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${theme.colors.greyVeryDark};
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: ${theme.colors.orange};
    color: ${theme.colors.orange};
    background: rgba(249, 151, 70, 0.08);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-top: 16px;

  @media (max-width: 992px) {
    flex-direction: column;
    width: 100%;

    ${GhostButton} {
      width: 100%;
    }
  }
`;
